export interface UserProfile {
  uid: string;
  displayName: string | null;
  email: string | null;
  photoURL: string | null;
  completedStudyIds?: string[];
  studyProgress?: Record<string, number>; // studyId -> current segment index
  awards?: Award[];
  xp?: number;
  isAdmin?: boolean;
  orgId?: string; // The ID of the organization the user belongs to

  // Highlighting & Notes
  highlights?: Record<string, StudyHighlight[]>; // studyId -> array of highlights

  // Reading Preferences
  readingPreferences?: {
    fontSize: 'small' | 'medium' | 'large';
    theme: 'light' | 'sepia' | 'dark';
  };

  // Streak Tracking
  currentStreak?: number;
  longestStreak?: number;
  lastStudyDate?: number; // timestamp
  studyDates?: number[]; // array of timestamps for calendar view

  // Course Progress
  courseProgress?: Record<string, number>; // courseId -> current study index (0-based)
  completedCourseIds?: string[]; // Array of completed course IDs

  // CTA Tracking
  ctaInteractions?: Record<string, Record<string, CTAInteraction>>; // courseId -> ctaId -> interaction
}

export interface StudyHighlight {
  segmentIndex: number;
  text: string;
  color: string;
  createdAt: number;
}

// Subscription Tiers
export type SubscriptionTier = 'free' | 'ministry';

export interface SubscriptionInfo {
  tier: SubscriptionTier;
  upgradedAt?: number; // Timestamp when upgraded
  upgradedBy?: string; // Admin who upgraded the account
}

// Quota limits by tier
// Free tier: Lifetime starter credits (never reset - upgrade required when exhausted)
// Ministry tier: Monthly allowance (resets on 1st of each month)
export const TIER_LIMITS = {
  free: {
    images: 50,
    tts: 250,
    videos: 5,
    isLifetime: true, // Free tier credits never reset
  },
  ministry: {
    images: 250,
    tts: 1000,
    videos: 10,
    isLifetime: false, // Ministry tier resets monthly
  },
} as const;

export interface AIQuota {
  // For free tier: lifetime totals; For ministry: current month totals
  period: string; // 'lifetime' for free tier, 'YYYY-MM' for ministry
  imageCount: number;
  ttsCount: number;
  videoCount: number;
  lastResetAt: number; // Timestamp
  usageLog?: {
    timestamp: number;
    userId: string;
    feature: 'gemini-image' | 'google-tts' | 'veo-video';
    details?: string;
    imageUrl?: string;
    audioUrl?: string;
    videoUrl?: string;
  }[];
}

export interface GlobalAIQuota {
  date: string; // Format: YYYY-MM-DD
  count: number; // Total usage across all orgs
  limit: number; // Daily limit (default: 100)
  lastResetAt: number; // Timestamp
}

export interface NotificationSettings {
  welcomeEmail?: boolean;           // Email when user joins organization
  studyCompletionEmail?: boolean;   // Email when user completes a study
  courseCompletionEmail?: boolean;  // Email when user completes a course
  newStudyPublishedEmail?: boolean; // Email org members when new study published
  questionNotificationEmail?: boolean; // Email authors when they receive questions
  replyNotificationEmail?: boolean;    // Email users when their questions get replies
  ctaResponseEmail?: boolean;          // Email admins when someone submits a CTA response
}

export interface Organization {
  id: string;
  name: string;
  code: string; // 5-digit alphanumeric code
  ownerId: string;
  createdAt: number;
  adoptedStudyIds?: string[]; // IDs of studies from other orgs that are visible to this org
  aiQuota?: AIQuota; // AI usage tracking (lifetime for free, monthly for ministry)
  subscription?: SubscriptionInfo; // Subscription tier info
  notificationSettings?: NotificationSettings; // Email notification preferences
}

export interface Award {
  id: string;
  name: string;
  description: string;
  icon: string; // Icon name
  earnedAt: number;
}

export interface Question {
  id: string;
  studyId: string;
  segmentId: string;
  userId: string;
  userEmail: string | null;
  userName: string | null;
  questionText: string;
  createdAt: number;
  replies?: QuestionReply[];
  isRead?: boolean;
}

export interface QuestionReply {
  id: string;
  authorId: string;
  authorName: string;
  replyText: string;
  createdAt: number;
}

export type SegmentType = 'text' | 'image' | 'video' | 'verse' | 'quiz' | 'image-text' | 'question' | 'cta';

export enum VisibilityLevel {
  PRIVATE = 'private',           // Only creator can see
  ORG_ONLY = 'org_only',         // Only creator's org members can see
  ALL_ORGS = 'all_orgs',         // Any org members can see (admins can adopt)
  PUBLIC = 'public'              // Anyone on the internet can view (no auth required)
}

export interface StudySegment {
  id: string;
  type: SegmentType;
  content: string; // Text content, Image Base64, YouTube URL, or Quiz Question
  metadata?: {
    caption?: string;
    book?: string; // Bible book name (e.g., "John")
    chapter?: string; // Chapter number (e.g., "3")
    verses?: string; // Verse or verse range (e.g., "16" or "16-17")
    version?: string; // Bible version (e.g., "NIV", "KJV")
    prompt?: string; // For generated images
    imageStyle?: string;
    imageAspectRatio?: string;
    videoStyle?: string;
    videoResolution?: string;
    videoDuration?: number; // 4, 6, or 8 seconds
    videoAspectRatio?: string; // '16:9' or '9:16'
    videoNegativePrompt?: string; // Content to exclude from video
    quizOptions?: string[]; // Array of 4 strings for quiz
    correctAnswer?: number; // Index of correct answer (0-3)
    imageUrl?: string; // Base64 or URL for image in combined segments
    uiMode?: 'text' | 'image' | 'both'; // Control which editors are visible
    videoType?: 'youtube' | 'hosted'; // Type of video content
    questionPrompt?: string; // Prompt text for question segments
    questionPlaceholder?: string; // Placeholder text for question input
    audioScript?: string; // Script to be spoken over generated video
    // CTA segment fields
    ctaType?: CTAType; // Type of CTA (email_signup, church_invite, etc.)
    ctaConfig?: CTATypeConfig; // Type-specific configuration
    ctaButtonText?: string; // Custom button text (e.g., "Sign Up", "RSVP")
    ctaSkipText?: string; // Custom skip text (e.g., "Skip for now")
  };
  audioUrl?: string; // URL to pre-generated audio file in Firebase Storage
  audioVoice?: string; // Voice used for audio generation (e.g., "en-US-Chirp-3-HD")
  audioGeneratedAt?: number; // Timestamp when audio was generated
}

export interface Study {
  id: string;
  orgId: string; // Organization this study belongs to
  authorId: string;
  authorName: string;
  title: string;
  description: string;
  segments: StudySegment[];
  visibility: VisibilityLevel; // Controls who can view this study
  coverImage?: string; // Base64 data URI for the study card background
  coverImagePosition?: { x: number; y: number }; // Position offset for cover image (percentage-based)
  createdAt: number;
  tags: string[];
  clonedFrom?: string; // If this study was cloned from another, store the original study ID
  clonedAt?: number; // Timestamp when this study was cloned
}

export interface StudyMetrics {
  studyId: string;
  views: number; // Total number of times study was started
  completions: number; // Total number of times study was completed
  averageTimeSpent?: number; // Average time in seconds
  uniqueViewers: string[]; // Array of user IDs who have viewed
  segmentEngagement?: Record<number, number>; // segmentIndex -> time spent in seconds
  lastUpdated: number;
}

// CTA (Call To Action) Types
export type CTAType =
  | 'email_signup'      // Collect email address
  | 'account_creation'  // Prompt unauthenticated users to sign up
  | 'church_invite'     // Church/location info with optional RSVP
  | 'free_book'         // Collect mailing address for book
  | 'event_rsvp';       // Event details with RSVP form

// CTA Type-specific configurations
export interface EmailSignupConfig {
  type: 'email_signup';
  collectName?: boolean;
  successMessage?: string;
}

export interface AccountCreationConfig {
  type: 'account_creation';
  benefits?: string[];
}

export interface ChurchInviteConfig {
  type: 'church_invite';
  churchName: string;
  address?: string;
  serviceTime?: string;
  mapLink?: string;
  contactEmail?: string;
  contactPhone?: string;
  collectRsvp?: boolean;
  coverImage?: string;
}

export interface FreeBookConfig {
  type: 'free_book';
  bookTitle: string;
  bookDescription?: string;
  bookImage?: string;
  collectPhone?: boolean;
}

export interface EventRSVPConfig {
  type: 'event_rsvp';
  eventName: string;
  eventDate: string;
  eventTime?: string;
  eventLocation?: string;
  eventDescription?: string;
  mapLink?: string;
  coverImage?: string;
  maxAttendees?: number;
  collectPhone?: boolean;
  collectGuestCount?: boolean;
}

export type CTATypeConfig =
  | EmailSignupConfig
  | AccountCreationConfig
  | ChurchInviteConfig
  | FreeBookConfig
  | EventRSVPConfig;

// CTA Configuration (stored in course)
export interface CTAConfig {
  id: string;
  type: CTAType;
  title: string;
  description?: string;
  buttonText?: string;
  skipText?: string;
  config: CTATypeConfig;
}

export interface Course {
  id: string;
  orgId: string; // Organization this course belongs to
  authorId: string;
  authorName: string;
  title: string;
  description: string;
  studyIds: string[]; // Ordered array of study IDs
  coverImage?: string; // Base64 data URI for the course card background
  coverImagePosition?: { x: number; y: number }; // Position offset for cover image (percentage-based)
  visibility: VisibilityLevel; // Controls who can view this course
  createdAt: number;
  tags: string[];
  requireSequentialViewing?: boolean; // If true, users must complete studies in order
}

// CTA Response types (stored in ctaResponses collection)
export interface EmailSignupResponse {
  type: 'email_signup';
  email: string;
  name?: string;
}

export interface ChurchRsvpResponse {
  type: 'church_invite';
  name: string;
  email?: string;
  phone?: string;
  rsvpStatus: 'yes' | 'maybe' | 'no';
  notes?: string;
}

export interface FreeBookResponse {
  type: 'free_book';
  name: string;
  email: string;
  address: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country?: string;
  };
  phone?: string;
}

export interface EventRsvpResponse {
  type: 'event_rsvp';
  name: string;
  email: string;
  phone?: string;
  guestCount?: number;
  rsvpStatus: 'yes' | 'maybe' | 'no';
  notes?: string;
}

export type CTAResponseData =
  | EmailSignupResponse
  | ChurchRsvpResponse
  | FreeBookResponse
  | EventRsvpResponse;

export interface CTAResponse {
  id: string;
  orgId: string;
  studyId: string;
  segmentId: string;
  ctaType: CTAType;
  userId?: string;
  respondedAt: number;
  response: CTAResponseData;
}

// CTA Interaction tracking (stored in UserProfile)
// Key structure: studyId -> segmentId -> CTAInteraction
export interface CTAInteraction {
  status: 'skipped' | 'completed';
  timestamp: number;
  responseId?: string;
}

export interface CourseMetrics {
  courseId: string;
  views: number; // Total number of times course was started
  completions: number; // Total number of times course was completed
  uniqueViewers: string[]; // Array of user IDs who have viewed
  lastUpdated: number;
}

// Media Manager Types
export type MediaType = 'image' | 'video' | 'audio';
export type MediaSourceType = 'cover' | 'segment' | 'standalone' | 'video-reference';

export interface MediaItem {
  id: string;
  orgId: string;
  mediaType: MediaType;
  url: string;
  storagePath: string;
  uploadedBy: string;
  uploadedAt: number;
  prompt?: string;
  isAIGenerated: boolean;
  sourceType: MediaSourceType;
  associatedStudyId?: string;
  associatedCourseId?: string;
  segmentId?: string;
  fileName?: string;
  fileSize?: number;
  dimensions?: { width: number; height: number };
  duration?: number;
}

export enum ViewState {
  DASHBOARD = 'DASHBOARD',
  STUDY_EDITOR = 'STUDY_EDITOR',
  STUDY_VIEWER = 'STUDY_VIEWER',
  AUTH = 'AUTH',
  PROFILE = 'PROFILE',
  ONBOARDING = 'ONBOARDING',
  COURSE_EDITOR = 'COURSE_EDITOR',
  COURSE_VIEWER = 'COURSE_VIEWER',
  PUBLIC_GALLERY = 'PUBLIC_GALLERY',
  AI_USAGE_ADMIN = 'AI_USAGE_ADMIN',
  QUESTIONS_MANAGER = 'QUESTIONS_MANAGER',
  MEDIA_MANAGER = 'MEDIA_MANAGER',
  ANALYTICS_ADMIN = 'ANALYTICS_ADMIN'
}

// Analytics Types
export interface OrganizationAnalytics {
  overview: {
    totalStudies: number;
    totalCourses: number;
    totalViews: number;
    totalCompletions: number;
    uniqueLearners: number;
    averageCompletionRate: number;
  };
  studies: StudyAnalyticsItem[];
  courses: CourseAnalyticsItem[];
  questions: QuestionAnalytics;
  learners: LearnerStats;
}

export interface StudyAnalyticsItem {
  studyId: string;
  title: string;
  authorName: string;
  views: number;
  completions: number;
  uniqueViewers: number;
  completionRate: number;
  createdAt: number;
}

export interface CourseAnalyticsItem {
  courseId: string;
  title: string;
  authorName: string;
  views: number;
  completions: number;
  uniqueViewers: number;
  completionRate: number;
  studyCount: number;
  createdAt: number;
}

export interface QuestionAnalytics {
  totalQuestions: number;
  unreadQuestions: number;
  answeredQuestions: number;
  responseRate: number;
}

export interface LearnerStats {
  totalMembers: number;
  activeThisWeek: number;
  topLearners: Array<{
    displayName: string;
    xp: number;
    completedCount: number;
  }>;
}